// Owned-device → imported-listing mapping (Back Market catalog import, plan T12).
//
// Given a `user_products.variant_id`, resolve the imported catalog listing it
// belongs to so an owned device can deep-link into the Buy feed ("view in Buy").
//
// This reads the EXISTING `product_variants` / `product_models` tables directly
// (the same raw-Supabase path `variantOptions.ts` uses) — NOT the new service-role-only
// catalog tables (colors / product_specs / product_images / category_axis_roles),
// which are reachable only through the backend `/catalog` API. A listing is
// "imported" iff its `product_models.listing_key` is set; legacy scan/Apple
// models have a NULL `listing_key` and resolve to `null` here, so the affordance
// only appears when a real Buy listing exists.

import { supabase } from './supabase';

export type ResolvedListing = {
  listingId: string;
  listingTitle: string;
  // Echo the owned variant back so "View in Buy" can preselect it inside the
  // consolidated listing (one card now holds many configs).
  variantId: string;
};

// The new T1 columns aren't in the generated DB types yet, so type the row we
// read explicitly rather than casting the whole query loosely.
type ModelListingRow = {
  id: string;
  listing_key: string | null;
  listing_title: string | null;
};

/**
 * Resolve the imported listing for an owned device's variant, or null when the
 * variant has no model, the model wasn't imported (NULL listing_key), or any
 * read fails. Never throws — the caller treats null as "no Buy listing".
 */
export async function resolveListingForVariant(
  variantId: string | null | undefined
): Promise<ResolvedListing | null> {
  if (!variantId) return null;

  try {
    const { data: variantRow, error: variantErr } = await supabase
      .from('product_variants')
      .select('model_id')
      .eq('id', variantId)
      .maybeSingle();

    if (variantErr) {
      console.error('[catalogListings] variant lookup failed:', variantErr);
      return null;
    }
    const modelId = variantRow?.model_id ?? null;
    if (!modelId) return null;

    const { data: modelRow, error: modelErr } = await supabase
      .from('product_models')
      .select('id, listing_key, listing_title')
      .eq('id', modelId)
      .maybeSingle<ModelListingRow>();

    if (modelErr) {
      console.error('[catalogListings] model lookup failed:', modelErr);
      return null;
    }
    // Only imported listings carry a listing_key; everything else has no Buy page.
    if (!modelRow || !modelRow.listing_key) return null;

    return {
      listingId: modelRow.id,
      listingTitle: modelRow.listing_title ?? '',
      variantId,
    };
  } catch (err) {
    console.error('[catalogListings] resolveListingForVariant threw:', err);
    return null;
  }
}
